import * as React from "react";
import { CircleBtn, CircleBtnType } from "../../../../../../../react-core/Html/CircleBtn";
import { MoneyTextBoxEditor } from "../../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { BenefitCrystallisationEventTotal } from "../../../../../../../Wrap/Models/Domains/Drawdown/BenefitCrystallisationEventTotal";
import { CustomerPensionProtectionModel } from "../../../../../../../Wrap/Models/Domains/Drawdown/CustomerPensionProtectionModel";
import { PrimaryPclsUsage } from "../../../../../../../Wrap/Models/Domains/Drawdown/PrimaryPclsUsage";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { TaxYear } from "./TaxYear";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { CustomerPensionFixedProtection } from "../../../../../../../Wrap/Models/Domains/Drawdown/CustomerPensionFixedProtection";
import { PensionProtectionInformation } from "../../../ClientRecord/Resource/Contents";

interface IProtectionInformationProps extends IProcessComponentProps {
    data: DrawdownModel;
    onChange: (changes) => void;
    process: ProcessType;
    pensionProtection: CustomerPensionProtectionModel;
}

interface IProtectionInformationState {
    hasChanged: boolean;
}

export class ProtectionInformation extends ProcessComponent<IProtectionInformationProps, IProtectionInformationState> {
    constructor(props: any) {
        super(props);
        this.state = {
            hasChanged: false
        };
    }

    private handleChange(changes) {
        const { data, onChange } = this.props;
        this.onDataChange(onChange, changes, data);
    }

    private onTaxYearChange(v, index, field) {
        const _data = this.props.data[field].concat();
        if (v == -1) {
            _data.splice(index, 1);
        } else {
            for (const key in v) {
                _data[index][key] = v[key];
            }
        }
        if (field == "BceOffPlatform") {
            this.setState({ hasChanged: true });
        }
        this.props.onChange({ [field]: _data });
    }

    private renderTaxYear(data: PrimaryPclsUsage[] | BenefitCrystallisationEventTotal[], key) {
        const renderedYears = data.map((y) => y.Year);
        return data.map((ele, index) => {
            return (
                <div key={`tax-year-${index}`}>
                    <TaxYear
                        data={ele}
                        excludeYears={renderedYears}
                        onChange={(v) => this.onTaxYearChange(v, index, key)}
                        onRef={(ref) => this.appendChild(ref)}
                    />
                    <CircleBtn
                        type={CircleBtnType.remove}
                        cssWrapper="mr-52"
                        title="Remove this tax year"
                        onClick={() => this.onTaxYearChange(-1, index, key)}
                    />
                </div>
            );
        });
    }

    // Not displayed where the member has Enhanced Protection with PCLS Protection
    private showOffPlatformBCE = (pensionProtection: CustomerPensionProtectionModel) => {
        return !(this.hasEnhancedProtection(pensionProtection) && this.hasEnhancedPCLSProtection(pensionProtection));
    };

    // BCEs before april 2024 = yes, allowance amount = no AND
    // no transitional tax-free allowance cert AND
    // (has enhanced protection without protected PCLS without tax free cert OR
    // (has primary protection without tax free cert)
    private showNotional = (pensionProtection: CustomerPensionProtectionModel) => {
        const { data } = this.props;
        if (!data?.AllowanceDetails?.HasOffPlatformBcePriorApril2024 || data?.AllowanceDetails?.AwareOfBCEAmounts) {
            return false;
        }
        return (
            (this.hasEnhancedProtection(pensionProtection) &&
                !this.hasEnhancedPCLSProtection(pensionProtection) &&
                !this.hasTransitionalTaxFreeAllowanceCertificate(pensionProtection)) ||
            (this.hasPrimaryProtection(pensionProtection) &&
                !this.hasTransitionalTaxFreeAllowanceCertificate(pensionProtection))
        );
    };

    private hasPrimaryProtection = (pensionProtection: CustomerPensionProtectionModel) => {
        return pensionProtection?.Primary?.HasProtection;
    };

    private hasPrimaryPCLSProtection = (pensionProtection: CustomerPensionProtectionModel) => {
        return pensionProtection?.Primary?.ProtectionAmount > 0;
    };

    private hasEnhancedProtection = (pensionProtection: CustomerPensionProtectionModel) => {
        return pensionProtection?.Enhanced?.HasProtection;
    };

    private hasEnhancedProtectionBeenAppliedBefore15032023 = (pensionProtection: CustomerPensionProtectionModel) => {
        return pensionProtection?.Enhanced?.HasBeenAppliedBefore15032023;
    };

    private hasEnhancedPCLSProtection = (pensionProtection: CustomerPensionProtectionModel) => {
        return pensionProtection?.Enhanced?.EntitlementPercentage;
    };

    private fixedProtection2016 = (pensionProtection: CustomerPensionProtectionModel): CustomerPensionFixedProtection[] => {
        return pensionProtection?.Fixed?.filter((x) => x.Year == 2016 && x?.HasProtection);
    };

    private fixedProtection2014 = (pensionProtection: CustomerPensionProtectionModel): CustomerPensionFixedProtection[] => {
        return pensionProtection?.Fixed?.filter((x) => x.Year == 2014 && x?.HasProtection);
    };

    private fixedProtection2012 = (pensionProtection: CustomerPensionProtectionModel): CustomerPensionFixedProtection[] => {
        return pensionProtection?.Fixed?.filter((x) => x.Year == 2012 && x?.HasProtection);
    };

    private hasFixedProtectionBeenAppliedBefore15032023 = (fixedProtection: CustomerPensionFixedProtection) => {
        return fixedProtection?.HasBeenAppliedBefore15032023;
    };

    private hasTransitionalTaxFreeAllowanceCertificate = (pensionProtection: CustomerPensionProtectionModel) => {
        return pensionProtection?.TransitionalLumpSum?.HasProtection;
    };

    private showSeriousIllHealthLumpSum = (data: DrawdownModel, pensionProtection: CustomerPensionProtectionModel) => {
        return (
            data?.BceOffPlatform &&
            data.BceOffPlatform.length > 0 &&
            this.hasPrimaryProtection(pensionProtection) &&
            !this.hasPrimaryPCLSProtection(pensionProtection)
        );
    };

    private showPCLSOffPlatform = (data: DrawdownModel, pensionProtection: CustomerPensionProtectionModel) => {
        return (
            this.hasPrimaryProtection(pensionProtection) &&
            this.hasPrimaryPCLSProtection(pensionProtection) &&
            data?.AllowanceDetails?.HasOffPlatformBcePriorApril2024 &&
            !data?.AllowanceDetails?.AwareOfBCEAmounts
        );
    };

    private getProtectionInformationMessages = (pensionProtection: CustomerPensionProtectionModel): string[] => {
        const messages: string[] = [];

        if (this.hasEnhancedProtection(pensionProtection)) {
            if (this.hasEnhancedProtectionBeenAppliedBefore15032023(pensionProtection)) {
                if (this.hasEnhancedPCLSProtection(pensionProtection)) {
                    messages.push(
                        PensionProtectionInformation.enhancedProtectionAppliedBefore15032023WithPCLS(pensionProtection?.Enhanced?.EntitlementPercentage)
                    );
                } else {
                    messages.push(
                        PensionProtectionInformation.enhancedProtectionAppliedBefore15032023WithNoPCLS
                    );
                }
            } else {
                if (this.hasEnhancedPCLSProtection(pensionProtection)) {
                    messages.push(
                        PensionProtectionInformation.enhancedProtectionAppliedOnOrAfter15032023WithPCLS(pensionProtection?.Enhanced?.EntitlementPercentage)
                    );
                } else {
                    messages.push(
                        PensionProtectionInformation.enhancedProtectionAppliedOnOrAfter15032023WithNoPCLS
                    );
                }
            }
        }

        const fixedProtection2016 = this.fixedProtection2016(pensionProtection);
        if (fixedProtection2016 && fixedProtection2016.length > 0) {
            if (this.hasFixedProtectionBeenAppliedBefore15032023(fixedProtection2016[0])) {
                messages.push(
                    PensionProtectionInformation.fixedProtection2016AppliedBefore15032023
                );
            } else {
                messages.push(
                    PensionProtectionInformation.fixedProtection2016AppliedOnOrAfter15032023
                );
            }
        }

        const fixedProtection2014 = this.fixedProtection2014(pensionProtection);
        if (fixedProtection2014 && fixedProtection2014.length > 0) {
            if (this.hasFixedProtectionBeenAppliedBefore15032023(fixedProtection2014[0])) {
                messages.push(
                    PensionProtectionInformation.fixedProtection2014AppliedBefore15032023
                );
            } else {
                messages.push(
                    PensionProtectionInformation.fixedProtection2014AppliedOnOrAfter15032023
                );
            }
        }

        const fixedProtection2012 = this.fixedProtection2012(pensionProtection);
        if (fixedProtection2012 && fixedProtection2012.length > 0) {
            if (this.hasFixedProtectionBeenAppliedBefore15032023(fixedProtection2012[0])) {
                messages.push(
                    PensionProtectionInformation.fixedProtection2012AppliedBefore15032023
                );
            } else {
                messages.push(
                    PensionProtectionInformation.fixedProtection2012AppliedOnOrAfter15032023
                );
            }
        }

        return messages;
    };
    
    public render(): JSX.Element {
        const { data, pensionProtection, onChange } = this.props;
        const messages = this.getProtectionInformationMessages(pensionProtection);

        return (
            <>
                {/*Have there been any Benefit Crystallisation Events (BCEs) off platform prior to 6 April 2024?*/}
                {this.showOffPlatformBCE(pensionProtection) && (
                    <>
                        <RadiosEditor
                            options={[true, false]}
                            cssWrapper={"rg-inline mb-10"}
                            onChange={(v) => {
                                this.onDataChange(
                                    onChange,
                                    { HasOffPlatformBcePriorApril2024: v },
                                    data,
                                    "AllowanceDetails"
                                );
                            }}
                            selected={this.hasTransitionalTaxFreeAllowanceCertificate(pensionProtection) ? true : data?.AllowanceDetails?.HasOffPlatformBcePriorApril2024}
                            disabled={this.hasTransitionalTaxFreeAllowanceCertificate(pensionProtection)}
                            label="Have there been any Benefit Crystallisation Events (BCEs) off platform prior to 6 April 2024?"
                            getText={(o) => (o ? "Yes" : "No")}
                            required={"Please select"}
                        />
                        {data?.AllowanceDetails?.HasOffPlatformBcePriorApril2024 && (
                            <RadiosEditor
                                options={[true, false]}
                                cssWrapper={"rg-inline mb-10"}
                                onChange={(v) => {
                                    if (v === true) {
                                        onChange({
                                            SlaUsedOffPlatform: 0
                                        });
                                    }
                                    this.onDataChange(
                                        onChange,
                                        { AwareOfBCEAmounts: v },
                                        data,
                                        "AllowanceDetails"
                                    );
                                }}
                                selected={this.hasTransitionalTaxFreeAllowanceCertificate(pensionProtection) ? true : data?.AllowanceDetails?.AwareOfBCEAmounts}
                                disabled={this.hasTransitionalTaxFreeAllowanceCertificate(pensionProtection)}
                                label="Do you know the allowance amounts for these BCEs?"
                                getText={(o) => (o ? "Yes" : "No")}
                                required={"Please select"}
                            />
                        )}
                    </>
                )}

                {/*Notional value of benefits from before 6 April 2006*/}
                {this.showNotional(pensionProtection) && (
                    <>
                        <MoneyTextBoxEditor
                            cssWrapper="control-size-245"
                            label="
                        Notional value of benefits from before 6 April 2006"
                            currency={CurrencyCode.GBP}
                            value={data?.NotionalValueBeforeApril2006?.Amount}
                            onChange={(v) =>
                                this.handleChange({
                                    NotionalValueBeforeApril2006: {
                                        Amount: v,
                                        Currency: CurrencyCode.GBP,
                                    },
                                })
                            }
                             validationRules={[
                                {
                                    errorMessage: "Please enter an amount that is greater than zero.",
                                    validator: (v) => v >= 0,
                                },
                            ]}
                        />
                        <RadiosEditor
                            options={[true, false]}
                            cssWrapper={"rg-inline mb-10"}
                            onChange={(v) => {
                                this.setState({ hasChanged: true });
                                onChange({ BceOffPlatform: v ? [{ Amount: null, Year: 2006 }] : null });
                            }}
                            selected={data.BceOffPlatform && data.BceOffPlatform.length > 0 ? true : false}
                            label="Has any benefit crystallisation been performed off platform since 6 April 2006?"
                            getText={(o) => (o ? "Yes" : "No")}
                            required={"Please select"}
                        />
                        {data?.BceOffPlatform && data.BceOffPlatform.length > 0 && (
                            <>
                                {this.renderTaxYear(data.BceOffPlatform, "BceOffPlatform")}
                                <CircleBtn
                                    type={CircleBtnType.add}
                                    title="Add another tax year"
                                    onClick={() => {
                                        const _data = this.props.data.BceOffPlatform.concat();
                                        _data.push({ Amount: null, Year: null });
                                        this.props.onChange({ BceOffPlatform: _data });
                                        this.setState({ hasChanged: true });
                                    }}
                                />
                            </>
                        )}
                    </>
                )}

                {/*Was part of this amount in respect of a Serious Ill Health Lump Sum taken prior to age 75?*/}
                {this.showSeriousIllHealthLumpSum(data, pensionProtection) && (
                    <>
                        <RadiosEditor
                            options={[true, false]}
                            cssWrapper={"rg-inline mb-10"}
                            onChange={(v) => {
                                this.setState({ hasChanged: true });
                                onChange({
                                    SeriousIllHealthLumpSumTakenPriorTo75: v ? [{ Amount: null, Year: 2006 }] : null,
                                });
                            }}
                            selected={
                                data.SeriousIllHealthLumpSumTakenPriorTo75 &&
                                data.SeriousIllHealthLumpSumTakenPriorTo75.length > 0
                                    ? true
                                    : false
                            }
                            label="Was part of this amount in respect of a Serious Ill Health Lump Sum taken prior to age 75?"
                            getText={(o) => (o ? "Yes" : "No")}
                            required={"Please select"}
                        />
                        {data?.SeriousIllHealthLumpSumTakenPriorTo75 &&
                            data.SeriousIllHealthLumpSumTakenPriorTo75.length > 0 && (
                                <>
                                    {this.renderTaxYear(
                                        data.SeriousIllHealthLumpSumTakenPriorTo75,
                                        "SeriousIllHealthLumpSumTakenPriorTo75"
                                    )}
                                    <CircleBtn
                                        type={CircleBtnType.add}
                                        title="Add another tax year"
                                        onClick={() => {
                                            const _data = this.props.data.SeriousIllHealthLumpSumTakenPriorTo75.concat();
                                            _data.push({ Amount: null, Year: null });
                                            this.props.onChange({ SeriousIllHealthLumpSumTakenPriorTo75: _data });
                                            this.setState({ hasChanged: true });
                                        }}
                                    />
                                </>
                            )}
                    </>
                )}

                {/*PCLS taken off platform per tax year*/}
                {this.showPCLSOffPlatform(data, pensionProtection) && (
                    <>
                        <RadiosEditor
                            options={[true, false]}
                            cssWrapper={"rg-inline mb-10"}
                            onChange={(v) => onChange({ OffPlatformPcls: v ? [{ Amount: null, Year: 2006 }] : null })}
                            selected={data?.OffPlatformPcls && data?.OffPlatformPcls.length > 0 ? true : false}
                            label="Has any protected PCLS been taken off platform since 2006 / 2007?"
                            getText={(o) => (o ? "Yes" : "No")}
                            required={"Please select"}
                        />
                        {data?.OffPlatformPcls && data.OffPlatformPcls.length > 0 && (
                            <>
                                {this.renderTaxYear(data.OffPlatformPcls, "OffPlatformPcls")}
                                <CircleBtn
                                    type={CircleBtnType.add}
                                    title="Add another tax year"
                                    onClick={() => {
                                        const _data = this.props.data.OffPlatformPcls.concat();
                                        _data.push({ Amount: null, Year: null });
                                        this.props.onChange({ OffPlatformPcls: _data });
                                    }}
                                />
                            </>
                        )}
                    </>
                )}                      

                {messages?.length > 0 && messages.map((message, idx) => {
                    return (
                        <Alert key={idx} alertType={AlertType.Warning} message={message} />
                    );
                })}
            </>
        );
    }
}
